import { AttributesSelector, Box, Flex, Text, Input } from '@workwhile/ui'
import React, { useMemo } from 'react'
import {
  FieldValues,
  FormProvider,
  Path,
  PathValue,
  useFormContext,
} from 'react-hook-form'

export interface StringFilterProps<FilterSchema extends FieldValues> {
  fieldPath: Path<FilterSchema>
  label: string
  filterKey: string
  inputLabel?: string
  formatValue?: (value: string) => string
}

export const StringFilter = <FilterSchema extends FieldValues>({
  fieldPath,
  label,
  filterKey,
  inputLabel,
  formatValue = (value) => value,
}: StringFilterProps<FilterSchema>) => {
  const methods = useFormContext<FilterSchema>()

  const value = methods.watch(fieldPath) as string | null | undefined

  const resetToDefaultValues = () => {
    methods.setValue(
      fieldPath,
      '' as PathValue<FilterSchema, Path<FilterSchema>>
    )
  }

  const selectedAttributes = useMemo(() => {
    const attributes: string[] = []
    const raw = value ?? ''
    if (raw.trim().length > 0) {
      const formatted = formatValue(raw)
      if (formatted) {
        attributes.push(formatted)
      }
    }
    return attributes
  }, [value, formatValue])

  return (
    <FormProvider {...methods}>
      <AttributesSelector
        label={label}
        rootGroupTitle="Filter by"
        selectedAttributes={selectedAttributes}
        onClearAll={resetToDefaultValues}
        groups={
          <AttributesSelector.Group
            key={filterKey}
            title={label}
            numberOfSelectedAttributes={selectedAttributes.length}
          >
            <Box>
              <Flex flexDirection="column" gap={2}>
                <Box>
                  {label ? (
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      {label}
                    </Text>
                  ) : null}
                  <Input
                    type="text"
                    placeholder={inputLabel ? `Enter ${inputLabel}` : undefined}
                    value={value ?? ''}
                    onChange={(e) => {
                      methods.setValue(
                        fieldPath,
                        e.target.value as PathValue<
                          FilterSchema,
                          Path<FilterSchema>
                        >
                      )
                    }}
                  />
                </Box>
              </Flex>
            </Box>
          </AttributesSelector.Group>
        }
      />
    </FormProvider>
  )
}
